import {
	type InstanceSelection,
	isReadable,
	type OfferedInstances,
	type PackageResourceResponse,
	type ResourceInstanceRef,
	unsupplied,
} from "@southfields-digital/mxmz-resource-model";

/** Translations the selection leaves out: the graphic airs its datafield defaults. */
export const unrouted = (translations: PackageResourceResponse[], selection: InstanceSelection) =>
	translations.filter((translation) => !selection[translation.id]);

/**
 * What the operator can choose from now, which is the latest release of each
 * configured major — not the revision the rundown pinned. Labels only, so this
 * snapshot can say what a selection misses and can never build a payload.
 */
export const offeredByLatestRelease = (
	translations: PackageResourceResponse[],
): OfferedInstances<ResourceInstanceRef> =>
	Object.fromEntries(
		translations.map((translation) => [
			translation.id,
			isReadable(translation) ? { instances: translation.instances } : null,
		]),
	);

/** Columns to route by: what the package configures, plus selections it no longer does. */
export const columnsFor = (
	translations: PackageResourceResponse[],
	offered: OfferedInstances<ResourceInstanceRef>,
	selections: InstanceSelection[],
): PackageResourceResponse[] => {
	const unconfigured = new Set(
		selections.flatMap((selection) =>
			unsupplied(selection, offered)
				.filter((entry) => entry.reason === "unknown-resource")
				.map((entry) => entry.resourceId),
		),
	);

	// An unreadable one reads as unknown too, and it already has a column.
	for (const translation of translations) unconfigured.delete(translation.id);

	return [
		...translations,
		...[...unconfigured].map((id) => ({ id, error: "not configured by this package" })),
	];
};
